import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, sendOtp, verifyOtp, resetPassword } from '../api/auth';
import appIcon from '@shared-ui/assets/app-icon.png';
import { useGoogleSignIn, LoginPage } from '@shared-ui/login';
import './Login.css';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID_WEB || '';

/**
 * Admin-console auth.
 *
 * - LOGIN: the existing password form, unchanged — the super-admin still signs
 *   in with the `admin` username + password (and Google, when configured).
 * - REGISTER: reuses the shared LoginPage register flow (email → OTP → password)
 *   so an invited admin can onboard. An admin invite links here with `?invite=`;
 *   the backend accepts role 'admin' only for an already-invited admin.
 */
export function Login() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invite = params.get('invite') ?? undefined;
  const [mode, setMode] = useState<'login' | 'register'>(invite ? 'register' : 'login');

  // ── Login form (unchanged password flow) ──
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = useCallback(async (loginEmail: string, loginPassword: string) => {
    await authApi.login(loginEmail, loginPassword);
    navigate('/');
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await handleLogin(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (response: { credential: string }) => {
    setError('');
    setIsLoading(true);
    try {
      await authApi.googleLogin(response.credential);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  const googleButtonRef = useGoogleSignIn(GOOGLE_CLIENT_ID, handleGoogleCredential);

  // ── Register (invited admins) — shared flow ──
  const handleRegister = useCallback(async (name: string, regEmail: string, regPassword: string, verificationToken: string) => {
    await authApi.register(name, regEmail, regPassword, verificationToken);
    navigate('/');
  }, [navigate]);

  if (mode === 'register') {
    return (
      <LoginPage
        subtitle="Admin Console"
        registerSubtitle="Admin Registration"
        mode="register"
        defaultEmail={invite}
        inviteBrandName={params.get('tenant') ?? undefined}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onGoogleLogin={(credential) => handleGoogleCredential({ credential })}
        onSendOtp={sendOtp}
        onVerifyOtp={verifyOtp}
        onResetPassword={resetPassword}
        googleClientId={GOOGLE_CLIENT_ID}
        footer={
          <p className="register-link">
            Already have an account?{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); setMode('login'); }}>Sign in</a>
          </p>
        }
      />
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <img src={appIcon} alt="BuzzBrain" className="login-logo" width="64" height="64" />
          <h1>BuzzBrain</h1>
          <span>Admin Console</span>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="text"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              required
              autoFocus
              data-testid="username-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              required
              data-testid="password-input"
            />
          </div>

          <button type="submit" disabled={isLoading} data-testid="login-submit">
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {GOOGLE_CLIENT_ID && (
          <>
            <div className="login-divider"><span>or</span></div>
            <div ref={googleButtonRef} className="google-signin-container" />
          </>
        )}

        <p className="register-link">
          Invited as an admin?{' '}
          <a href="#" onClick={(e) => { e.preventDefault(); setMode('register'); }}>Complete registration</a>
        </p>
      </div>
    </div>
  );
}
